<script setup lang="ts">
import { storeToRefs } from 'pinia';
import MenuList from '@layout/nav-v2/desktop/components/MenuList.vue';
import { getDistrict } from '@elements/address-utils.ts';
import Typography from '@components/Typography.vue';
import Divider from '@components/Divider.vue';
import { slugify } from '@tools/stringFormatter.ts';
import LocationSubmenuItem from '@layout/nav-v2/LocationSubmenuItem.vue';
import LocationHeaderLink from '@layout/nav-v2/LocationHeaderLink.vue';
import { trans, transchoice } from '@elements/translation.ts';
import { useNavbarStore } from '@stores/layout/useNavbarStore';

const {
  parisAreaUrl,
  parisDistricts,
  parisPois,
  idfAreaUrl,
  idfCities,
  franceAreaUrl,
  franceCities,
  plusCoworkingParisUrl,
  plusPriceParisUrl,
} = storeToRefs(useNavbarStore());
</script>

<template>
  <div class="flex flex-col space-y-4">
    <div class="flex space-x-4">
      <div>
        <LocationHeaderLink :link="parisAreaUrl">
          {{ trans('user_menu.top.locations.paris.title') }}
        </LocationHeaderLink>

        <div class="mt-2 flex space-x-3">
          <MenuList
            :class="[
              'h-fit',
              'grid grid-cols-4'
            ]"
          >
            <li
              v-for="parisDistrict in parisDistricts"
              :key="parisDistrict"
            >
              <LocationSubmenuItem
                class="justify-center"
                :link="`location-bureau-paris-${parisDistrict}`"
              >
                {{ transchoice('user_menu.top.locations.paris.district', { district: getDistrict(parisDistrict) }) }}
              </LocationSubmenuItem>
            </li>
          </MenuList>

          <Divider
            :class="[
              '!h-12 !w-px',
              'self-center'
            ]"
            color="gray"
          />

          <MenuList class="flex flex-col">
            <li
              v-for="parisPoi in parisPois"
              :key="parisPoi"
            >
              <LocationSubmenuItem :link="`location-bureau-${slugify(parisPoi)}`">
                {{ parisPoi }}
              </LocationSubmenuItem>
            </li>
          </MenuList>
        </div>
      </div>

      <Divider
        class="!h-full !w-px"
        color="gray"
      />

      <div>
        <LocationHeaderLink :link="idfAreaUrl">
          {{ trans('user_menu.top.locations.ile_de_france.title') }}
        </LocationHeaderLink>

        <MenuList
          :class="[
            'mt-2',
            'flex flex-col'
          ]"
        >
          <li
            v-for="idfCity in idfCities"
            :key="idfCity"
          >
            <LocationSubmenuItem :link="`location-bureau-${slugify(idfCity)}`">
              {{ idfCity }}
            </LocationSubmenuItem>
          </li>
        </MenuList>
      </div>

      <Divider
        class="!h-full !w-px"
        color="gray"
      />

      <div>
        <LocationHeaderLink :link="franceAreaUrl">
          {{ trans('user_menu.top.locations.france.title') }}
        </LocationHeaderLink>

        <MenuList
          :class="[
            'mt-2',
            'grid grid-cols-2'
          ]"
        >
          <li
            v-for="franceCity in franceCities"
            :key="franceCity"
          >
            <LocationSubmenuItem :link="`location-bureau-${slugify(franceCity)}`">
              {{ franceCity }}
            </LocationSubmenuItem>
          </li>
        </MenuList>
      </div>
    </div>

    <div>
      <Typography
        variant="body-bold"
        class="p-2"
      >
        {{ trans('user_menu.plus') }}
      </Typography>

      <MenuList
        :class="[
          'mt-2',
          'w-fit',
          'flex flex-col'
        ]"
      >
        <li>
          <LocationSubmenuItem :link="plusCoworkingParisUrl">
            {{ trans('user_menu.top.locations.paris.coworking') }}
          </LocationSubmenuItem>
        </li>

        <li>
          <LocationSubmenuItem :link="plusPriceParisUrl">
            {{ trans('user_menu.top.locations.paris.office_price') }}
          </LocationSubmenuItem>
        </li>
      </MenuList>
    </div>
  </div>
</template>
